import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function newItem() {
  return {
    _key: crypto.randomUUID(),
    mode: "existing",   // "existing" | "new-variant" | "new-product"
    product_id: "",
    variant_id: "",
    product_name: "",
    variant_name: "",
    category: "",
    subcategory: "",
    quantity: 1,
    unit_purchase_price: 0,
    suggested_sale_price: 0,
    notes: "",
  };
}

export default function EncomendaForm({ open, onOpenChange, onSaved, initialItems }) {
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  const [shippingCost, setShippingCost] = useState(0);
  const [extraFees, setExtraFees] = useState(0);
  const [items, setItems] = useState([newItem()]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data || []);
      } catch {}
    })();
    setSupplier("");
    setNotes("");
    setShippingCost(0);
    setExtraFees(0);
    if (initialItems && initialItems.length) {
      setItems(initialItems.map((it) => ({ ...newItem(), ...it, _key: crypto.randomUUID() })));
    } else {
      setItems([newItem()]);
    }
  }, [open, initialItems]);

  const updateItem = (idx, patch) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const totalCost = useMemo(() => {
    const items_cost = items.reduce((a, it) => a + Number(it.quantity || 0) * Number(it.unit_purchase_price || 0), 0);
    return items_cost + Number(shippingCost || 0) + Number(extraFees || 0);
  }, [items, shippingCost, extraFees]);

  const submit = async (e) => {
    e.preventDefault();
    for (const it of items) {
      if (Number(it.quantity) <= 0) return toast.error("Quantidade tem de ser > 0");
      if (Number(it.unit_purchase_price) < 0) return toast.error("Preço de compra inválido");
      if (it.mode === "existing" && (!it.product_id || !it.variant_id)) return toast.error("Escolhe o produto e a variante");
      if (it.mode === "new-variant" && (!it.product_id || !it.variant_name.trim())) return toast.error("Escolhe o produto e escreve a nova variante");
      if (it.mode === "new-product" && !it.product_name.trim()) return toast.error("Nome do novo produto é obrigatório");
    }

    const payloadItems = items.map((it) => {
      const base = {
        quantity: Number(it.quantity),
        unit_purchase_price: Number(it.unit_purchase_price),
        suggested_sale_price: Number(it.suggested_sale_price || 0),
        notes: it.notes || "",
      };
      if (it.mode === "existing") return { ...base, product_id: it.product_id, variant_id: it.variant_id };
      if (it.mode === "new-variant") return { ...base, product_id: it.product_id, variant_name: it.variant_name };
      return { ...base, product_name: it.product_name, variant_name: it.variant_name || "Único", category: it.category, subcategory: it.subcategory };
    });

    setSaving(true);
    try {
      await api.post("/encomendas", {
        supplier,
        notes,
        shipping_cost: Number(shippingCost),
        extra_fees: Number(extraFees),
        items: payloadItems,
      });
      toast.success("Encomenda registada · stock atualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="encomenda-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nova encomenda</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fornecedor</Label>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Ex: O Boticário" data-testid="enc-supplier-input" />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Remessa Natal 2026" data-testid="enc-notes-input" />
            </div>
            <div className="space-y-2">
              <Label>Custo de envio (CVE)</Label>
              <Input type="number" step="0.01" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} data-testid="enc-shipping-input" />
            </div>
            <div className="space-y-2">
              <Label>Outras taxas (CVE)</Label>
              <Input type="number" step="0.01" value={extraFees} onChange={(e) => setExtraFees(e.target.value)} data-testid="enc-fees-input" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Itens da encomenda</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setItems((a) => [...a, newItem()])} data-testid="add-enc-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Adicionar item
              </Button>
            </div>
            <div className="space-y-3">
              {items.map((it, idx) => {
                const productOptions = products;
                const selectedProduct = productOptions.find((p) => p.id === it.product_id);
                const variantOptions = selectedProduct?.variants || [];
                return (
                  <div key={it._key} className="rounded-xl bg-secondary p-3 sm:p-4 space-y-3" data-testid={`enc-item-${idx}`}>
                    <div className="flex flex-wrap gap-2 items-center">
                      <ModePill active={it.mode === "existing"} onClick={() => updateItem(idx, { mode: "existing" })}>Produto existente</ModePill>
                      <ModePill active={it.mode === "new-variant"} onClick={() => updateItem(idx, { mode: "new-variant" })}>Nova variante</ModePill>
                      <ModePill active={it.mode === "new-product"} onClick={() => updateItem(idx, { mode: "new-product" })}>Novo produto</ModePill>
                      {items.length > 1 && (
                        <button type="button" onClick={() => setItems((a) => a.filter((_, i) => i !== idx))} className="ml-auto text-[hsl(var(--destructive))] hover:opacity-80" data-testid={`remove-enc-item-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {it.mode === "existing" && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Produto</Label>
                          <Select value={it.product_id} onValueChange={(v) => updateItem(idx, { product_id: v, variant_id: "" })}>
                            <SelectTrigger data-testid={`enc-product-select-${idx}`}><SelectValue placeholder="Escolher produto" /></SelectTrigger>
                            <SelectContent>
                              {productOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Variante</Label>
                          <Select value={it.variant_id} onValueChange={(v) => updateItem(idx, { variant_id: v })} disabled={!variantOptions.length}>
                            <SelectTrigger data-testid={`enc-variant-select-${idx}`}><SelectValue placeholder="Escolher variante" /></SelectTrigger>
                            <SelectContent>
                              {variantOptions.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {it.mode === "new-variant" && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Produto</Label>
                          <Select value={it.product_id} onValueChange={(v) => updateItem(idx, { product_id: v })}>
                            <SelectTrigger data-testid={`enc-nv-product-${idx}`}><SelectValue placeholder="Escolher produto" /></SelectTrigger>
                            <SelectContent>
                              {productOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Nome da nova variante</Label>
                          <Input value={it.variant_name} onChange={(e) => updateItem(idx, { variant_name: e.target.value })} placeholder="Ex: 150ml" data-testid={`enc-nv-variant-${idx}`} />
                        </div>
                      </div>
                    )}

                    {it.mode === "new-product" && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Nome do produto</Label>
                          <Input value={it.product_name} onChange={(e) => updateItem(idx, { product_name: e.target.value })} placeholder="Ex: Perfume Malbec" data-testid={`enc-np-name-${idx}`} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Categoria</Label>
                          <Input value={it.category} onChange={(e) => updateItem(idx, { category: e.target.value })} placeholder="Feminino / Masculino" data-testid={`enc-np-cat-${idx}`} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Subcategoria</Label>
                          <Input value={it.subcategory} onChange={(e) => updateItem(idx, { subcategory: e.target.value })} placeholder="Perfumes" data-testid={`enc-np-subcat-${idx}`} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Nome da variante</Label>
                          <Input value={it.variant_name} onChange={(e) => updateItem(idx, { variant_name: e.target.value })} placeholder="Ex: 100ml" data-testid={`enc-np-variant-${idx}`} />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Quantidade</Label>
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`enc-qty-${idx}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Preço compra (CVE)</Label>
                        <Input type="number" step="0.01" value={it.unit_purchase_price} onChange={(e) => updateItem(idx, { unit_purchase_price: e.target.value })} data-testid={`enc-cost-${idx}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Venda sugerida (CVE)</Label>
                        <Input type="number" step="0.01" value={it.suggested_sale_price} onChange={(e) => updateItem(idx, { suggested_sale_price: e.target.value })} data-testid={`enc-sell-${idx}`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl bg-secondary p-3 flex items-center justify-between">
            <div className="lb-overline flex items-center gap-1"><Sparkles className="w-3 h-3 text-[hsl(var(--accent))]" /> Total desta encomenda</div>
            <div className="font-display text-2xl text-[hsl(var(--primary))]" data-testid="enc-total-preview">
              {new Intl.NumberFormat("pt-PT", { maximumFractionDigits: 0 }).format(Math.round(totalCost))} CVE
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white" data-testid="save-encomenda-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Registar e adicionar ao stock
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ModePill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${active ? "bg-[hsl(var(--primary))] text-white border-transparent" : "bg-white border-border text-muted-foreground hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}
